import React, { useState, useMemo } from 'react';
import { BookmarkIcon } from './icons/InterfaceIcons';
import { parseTextWithImages } from './parseTextWithImages.jsx';

// FIX: Add explicit types for props to allow 'key' prop in map.
export const QuestionCard: React.FC<{ question: any; isBookmarked: boolean; onToggleBookmark: any; user: any; onNavigateToSignIn: any; }> = ({ question, isBookmarked, onToggleBookmark, user, onNavigateToSignIn }) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const [integerAnswer, setIntegerAnswer] = useState('');
    const [isChecked, setIsChecked] = useState(false);

    const getDifficultyColor = (difficulty) => {
        if (difficulty === 'Easy') return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
        if (difficulty === 'Medium') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
        if (difficulty === 'Hard') return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
        return 'bg-gray-100 text-gray-800 dark:bg-zinc-700 dark:text-zinc-300';
    }

    const handleCheckAnswer = () => {
        setIsChecked(true);
    }

    const isCorrectAnswer = useMemo(() => {
        if (question.type === 'Integer') {
            return integerAnswer.trim() === question.answer.trim();
        } else {
            return question.options[selectedOption] === question.answer;
        }
    }, [selectedOption, integerAnswer, question]);

    return (
        <div className="bg-brand-surface border border-brand-border rounded-xl p-6 transition-shadow hover:shadow-md relative">
            <button
                onClick={() => {
                    if (user) {
                        onToggleBookmark(question.id);
                    } else {
                        onNavigateToSignIn();
                    }
                }}
                className="absolute top-4 right-4 p-2 rounded-full text-brand-text-secondary hover:bg-brand-primary/10 hover:text-brand-primary transition-colors"
                aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
                title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
                <BookmarkIcon filled={isBookmarked} className="w-5 h-5" />
            </button>
            <div className="flex flex-wrap gap-2 mb-4 text-xs font-semibold">
                <span className="px-2 py-1 rounded-full bg-brand-primary/10 text-brand-primary">{question.subject}</span>
                <span className="px-2 py-1 rounded-full bg-brand-secondary/10 text-brand-secondary">{question.topic}</span>
                <span className={`px-2 py-1 rounded-full ${getDifficultyColor(question.difficulty)}`}>{question.difficulty}</span>
                <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-zinc-700 text-brand-text-secondary">{question.type}</span>
                <span className="px-2 py-1 rounded-full bg-gray-100 dark:bg-zinc-700 text-brand-text-secondary">QID:{question.id}</span>
            </div>
            <div className="text-brand-text-primary mb-4 pr-10">{parseTextWithImages(question.text)}</div>
            
            {question.type === 'Integer' ? (
                <div className="mb-4">
                    <input 
                        type="text" // Changed to text to allow leading/trailing zeros and handle non-numeric input gracefully
                        value={integerAnswer}
                        onChange={(e) => setIntegerAnswer(e.target.value)}
                        disabled={isChecked}
                        className="w-full px-3 py-2 rounded-lg border border-brand-border bg-brand-bg text-brand-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                        placeholder="Enter your answer"
                    />
                </div>
            ) : (
                <ul className="space-y-2 mb-4">
                    {question.options.map((option, index) => {
                        const isSelected = selectedOption === index;
                        const isOptionCorrect = question.answer === option;
                        let itemClass = 'flex items-start text-sm p-2 rounded-lg cursor-pointer transition-colors border';
                        if (isChecked) {
                            if (isOptionCorrect) {
                                itemClass += ' bg-green-100 dark:bg-green-900/30 border-green-500';
                            } else if (isSelected) {
                                itemClass += ' bg-red-100 dark:bg-red-900/30 border-red-500';
                            } else {
                                itemClass += ' border-transparent';
                            }
                        } else if (isSelected) {
                            itemClass += ' bg-brand-primary/10 border-brand-primary';
                        } else {
                            itemClass += ' border-transparent';
                        }

                        return (
                            <li key={index} className={itemClass} onClick={() => !isChecked && setSelectedOption(index)}>
                                <span className="font-semibold mr-2">{String.fromCharCode(65 + index)}.</span>
                                <div className="flex-1">{parseTextWithImages(option)}</div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {!isChecked ? (
                <button
                    onClick={handleCheckAnswer}
                    disabled={(question.type === 'Integer' && integerAnswer.trim() === '') || (question.type !== 'Integer' && selectedOption === null)}
                    className="text-sm font-semibold text-brand-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Check Answer
                </button>
            ) : (
                <div className={`mt-4 p-4 rounded-lg animate-fade-in ${
                    isCorrectAnswer 
                        ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800' 
                        : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
                }`}>
                    <div className={`text-sm font-bold ${
                        isCorrectAnswer ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
                    }`}>
                        {isCorrectAnswer ? 'Correct!' : 'Incorrect.'} The correct answer is: {parseTextWithImages(question.answer)}
                    </div>
                    {question.solution && 
                        <div className="mt-2">
                            <div className={`text-sm font-bold ${
                                isCorrectAnswer ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'
                            }`}>Explanation:</div>
                            <div className={`text-sm ${
                                isCorrectAnswer ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                            } mt-1`}>{parseTextWithImages(question.solution)}</div>
                        </div>
                    }
                </div>
            )}
        </div>
    );
};